import type { IRecommendationEvent } from '@/interface/IBetBuilderRecommendation';
import type { IEventResultScore, IMatch, ITournamentCenterEntranceMatch } from '@/interface/IMatch';
import type { EventResult, IMarketGroup, IOdds } from '@/interface/IOdds';
import type { IOutright, IOutrightTournament } from '@/interface/IOutright';
import type { ITournamentMatch } from '@/interface/ITournamentMatch';
import type { ITournamentOutright } from '@/interface/ITournamentOutright';
import type { ITranslation } from '@/interface/ITranslation';
import type { BetTypeGroup } from '@/interface/enum';
import { LanguageType, LeagueSize, MarketPage, MarketType, OddsStatus, SportType, TimezoneOffset, TournamentSize } from '@/interface/enum';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { getPriceOptions } from '@/libraries/oddsApi/priceOptionHelper';
import {
    addHour,
    convertDateFromSourceToUserTimeZone,
    getDateWithMonth,
    getNowWithGMT,
    parseDate,
    parseISOString,
} from '@/libraries/utils/date';
import type {
    EventResultTypeFragment, EventTypeFragment, MarketGroupTypeFragment, Maybe, OddsTypeFragment, OutrightTournamentTypeFragment, OutrightTypeFragment,
    RecommendationEventFragment,
    TournamentCenterEntranceEventTypeFragment, TournamentCenterOutrightFragment, TournamentEventTypeFragment, TranslationFragment,
} from '@/types';
import {
    EnumHandicapType,
} from '@/types';

export function getEventDate(showTime: string) {
    if (showTime) {
        try {
            return getDateWithMonth(parseDate(showTime, 'MM/dd/yyyy HH:mm', TimezoneOffset.Singapore));
        } catch {
            return showTime.substring(0, 5);
        }
    }
    return '';
}

export function isToday(absKickoffTime: string) {
    const today = getNowWithGMT(TimezoneOffset.Server).setHours(0, 0, 0, 0);
    const tomorrow = addHour(new Date(today), 36);
    return Date.parse(absKickoffTime) <= tomorrow.getTime();
}

function validateEvent(event: EventTypeFragment | TournamentEventTypeFragment) {
    if (!event.tournament) {
        throw new Error('tournament is null');
    }

    if (!event.tournament.sport) {
        throw new Error('sport is null');
    }

    if (!event.homeTeam || !event.awayTeam) {
        throw new Error('homeTeam or awayTeam is null');
    }
}

function formatEventStream(event: EventTypeFragment | TournamentEventTypeFragment) {
    if (!event.stream) {
        return null;
    }

    const { stream, id: eventId } = event;

    return {
        videoUrl: stream.videoUrl,
        betRadar: stream.betRadar ? {
            eventId,
            streamId: stream.betRadar.streamId,
            streamStatus: stream.betRadar.streamStatus,
            allowedCountries: stream.betRadar.allowedCountries,
        } : null,
        gLive: stream.gLive ? {
            eventId,
            isPlaying: stream.gLive.isPlaying,
        } : null,
        betConstruct: stream.betConstruct ? {
            eventId: stream.betConstruct.eventId,
            allowedCountries: stream.betConstruct.allowedCountries,
            isPlaying: stream.betConstruct.isPlaying,
        } : null,
        betGenius: stream.betGenius ? {
            eventId: stream.betGenius.eventId,
            allowedCountries: stream.betGenius.allowedCountries,
            isPlaying: stream.betGenius.isPlaying,
            providerEventId: stream.betGenius.providerEventId,
        } : null,
    };
}

export function formatEventResultScores(eventResults: Array<{
    id: number;
    liveHomeScore: number;
    liveAwayScore: number;
    marketGroup?: { id: number } | null;
}>): IEventResultScore[] {
    return eventResults.flatMap((eventResult) => {
        if (!eventResult.marketGroup) {
            return [];
        }

        return [{
            id: eventResult.id,
            marketGroupId: eventResult.marketGroup.id as BetTypeGroup,
            liveHomeScore: eventResult.liveHomeScore,
            liveAwayScore: eventResult.liveAwayScore,
        }];
    });
}

export function formatEvent(event: EventTypeFragment): IMatch {
    validateEvent(event);

    const handicapType = event.isLive ? event.mainMarketEventResult.liveHandicapType : event.mainMarketEventResult.nonLiveHandicapType;

    return {
        type: 'match',
        awayTeam: {
            countryName: event.awayTeam?.countryName ?? '',
            teamName: event.awayTeam?.teamName.map(formatTranslation) ?? [],
        },
        eventCode: event.eventCode,
        eventDate: getEventDate(event.showTime),
        eventId: event.id,
        hasStatistics: event.hasStatistics,
        homeTeam: {
            countryName: event.homeTeam?.countryName ?? '',
            teamName: event.homeTeam?.teamName.map(formatTranslation) ?? [],
        },
        isMixParlay: event.mainMarketEventResult.isMixParlay,
        leagueId: event.tournament.id,
        leagueName: event.tournament.tournamentName.map(formatTranslation),
        leagueInformation: event.tournament.tournamentInfo.map(formatTranslation),
        leagueSize: LeagueSize.fromOddsApi(event.tournament.tournamentSize),
        liveCourtMatchId: event.liveCourtMatchId,
        marketPage: getMarketPage(event),
        marketTypeCount: event.marketTypeCount,
        dynamicMarketCount: event.dynamicMarketCount ?? 0,
        mainMarketEventResult: {
            extraInfo: event.mainMarketEventResult.extraInfo ? {
                awayRedCards: event.mainMarketEventResult.extraInfo.awayRedCards,
                homeRedCards: event.mainMarketEventResult.extraInfo.homeRedCards,
                injuryTime: event.mainMarketEventResult.extraInfo.injuryTime,
                period: event.mainMarketEventResult.extraInfo.period,
                periodStartTime: event.mainMarketEventResult.extraInfo.periodStartTime,
                round: event.mainMarketEventResult.extraInfo.round,
                statisticsUrl: event.mainMarketEventResult.extraInfo.statisticsUrl ?? undefined,
            } : null,
            isMixParlay: event.mainMarketEventResult.isMixParlay,
            liveAwayScore: event.mainMarketEventResult.liveAwayScore,
            liveHomeScore: event.mainMarketEventResult.liveHomeScore,
        },
        isTvLive: !!(event.showTimeType & 0x4),
        isSboLive: !!(event.showTimeType & 0x8),
        isHomeFavorite: handicapType === EnumHandicapType.Home,
        isAwayFavorite: handicapType === EnumHandicapType.Away,
        showTime: event.showTime,
        showTimeType: event.showTimeType,
        sportType: SportType.fromOddsApi(event.tournament.sport!.sportType),
        eventResults: formatEventResultScores(event.eventResults),
        statisticsUrl: event.mainMarketEventResult.extraInfo?.statisticsUrl ?? '',
        isToday: isToday(event.absKickoffTime),
        isClosed: false,
        tournament: formatLeague(event.tournament),
        matchOrder: 0,
        isLive: event.isLive,
        stream: formatEventStream(event),
        isSimulation: event.isSimulation,
        realKickoffTime: convertDateFromSourceToUserTimeZone(parseISOString(event.realKickoffTime), TimezoneOffset.Server),
    };
}

export function formatTournamentMatch(event: TournamentEventTypeFragment): ITournamentMatch {
    validateEvent(event);

    return {
        type: 'match',
        awayTeam: {
            id: event.awayTeam!.id,
            countryName: event.awayTeam?.countryName ?? '',
            teamName: event.awayTeam?.teamName.map(formatTranslation) ?? [],
        },
        eventCode: event.eventCode,
        eventDate: getEventDate(event.showTime),
        eventId: event.id,
        hasStatistics: event.hasStatistics,
        homeTeam: {
            id: event.homeTeam!.id,
            countryName: event.homeTeam?.countryName ?? '',
            teamName: event.homeTeam?.teamName.map(formatTranslation) ?? [],
        },
        isMixParlay: event.mainMarketEventResult.isMixParlay,
        leagueId: event.tournament.id,
        leagueName: event.tournament.tournamentName.map(formatTranslation),
        leagueInformation: event.tournament.tournamentInfo.map(formatTranslation),
        leagueSize: LeagueSize.fromOddsApi(event.tournament.tournamentSize),
        liveCourtMatchId: event.liveCourtMatchId,
        marketPage: getMarketPage(event),
        marketTypeCount: event.marketTypeCount,
        mainMarketEventResult: {
            extraInfo: event.mainMarketEventResult.extraInfo ? {
                awayRedCards: event.mainMarketEventResult.extraInfo.awayRedCards,
                homeRedCards: event.mainMarketEventResult.extraInfo.homeRedCards,
                injuryTime: event.mainMarketEventResult.extraInfo.injuryTime,
                period: event.mainMarketEventResult.extraInfo.period,
                periodStartTime: event.mainMarketEventResult.extraInfo.periodStartTime,
                round: event.mainMarketEventResult.extraInfo.round,
                statisticsUrl: event.mainMarketEventResult.extraInfo.statisticsUrl ?? undefined,
            } : null,
            isMixParlay: event.mainMarketEventResult.isMixParlay,
            liveAwayScore: event.mainMarketEventResult.liveAwayScore,
            liveHomeScore: event.mainMarketEventResult.liveHomeScore,
        },
        isTvLive: !!(event.showTimeType & 0x4),
        isSboLive: !!(event.showTimeType & 0x8),
        isHomeFavorite: (event.isLive ? event.mainMarketEventResult.liveHandicapType : event.mainMarketEventResult.nonLiveHandicapType) === EnumHandicapType.Home,
        isAwayFavorite: (event.isLive ? event.mainMarketEventResult.liveHandicapType : event.mainMarketEventResult.nonLiveHandicapType) === EnumHandicapType.Away,
        showTime: event.showTime,
        showTimeType: event.showTimeType,
        sportType: SportType.fromOddsApi(event.tournament.sport!.sportType),
        eventResults: formatEventResultScores(event.eventResults),
        statisticsUrl: event.mainMarketEventResult.extraInfo?.statisticsUrl ?? '',
        isToday: isToday(event.absKickoffTime),
        isClosed: false,
        tournament: formatLeague(event.tournament),
        matchOrder: 0,
        isLive: event.isLive,
        stream: formatEventStream(event),
        isSimulation: event.isSimulation,
        absKickoffTime: parseISOString(event.absKickoffTime),
        realKickoffTime: convertDateFromSourceToUserTimeZone(parseISOString(event.realKickoffTime), TimezoneOffset.Server),
    };
}

function formatLeague(tournament: any): any {
    return {
        id: tournament.id,
        isOlympicSpecialTournament: false,
        isSpecialTournament: false,
        isTestOnly: false,
        sport: tournament.sport ? { id: 0 } : undefined,
        tournamentCode: '',
        tournamentInfo: tournament.tournamentInfo,
        tournamentName: tournament.tournamentName,
        tournamentNameEn: getTranslated(tournament.tournamentName, LanguageType.EN),
        tournamentSize: TournamentSize.fromOddsApi(tournament.tournamentSize),
        eventsCount: 0,
    };
}

export function formatTournamentCenterEntranceEvent(event: TournamentCenterEntranceEventTypeFragment): ITournamentCenterEntranceMatch {
    return {
        eventDate: getEventDate(event.showTime),
        eventId: event.id,
        leagueId: event.tournament.id,
        leagueName: event.tournament.tournamentName.map(formatTranslation),
        marketPage: getMarketPage(event),
        mainMarketEventResult: {
            extraInfo: event.mainMarketEventResult.extraInfo ? {
                awayRedCards: event.mainMarketEventResult.extraInfo.awayRedCards,
                homeRedCards: event.mainMarketEventResult.extraInfo.homeRedCards,
                injuryTime: event.mainMarketEventResult.extraInfo.injuryTime,
                period: event.mainMarketEventResult.extraInfo.period,
                periodStartTime: event.mainMarketEventResult.extraInfo.periodStartTime,
                round: event.mainMarketEventResult.extraInfo.round,
            } : null,
            isMixParlay: false,
            liveAwayScore: event.mainMarketEventResult.liveAwayScore,
            liveHomeScore: event.mainMarketEventResult.liveHomeScore,
        },
        isHomeFavorite: (event.isLive ? event.mainMarketEventResult.liveHandicapType : event.mainMarketEventResult.nonLiveHandicapType) === EnumHandicapType.Home,
        isAwayFavorite: (event.isLive ? event.mainMarketEventResult.liveHandicapType : event.mainMarketEventResult.nonLiveHandicapType) === EnumHandicapType.Away,
        sportType: event.tournament?.sport?.sportType ? SportType.fromOddsApi(event.tournament.sport.sportType) : SportType.Unknown,
        eventCode: event.eventCode,
        absKickoffTime: parseISOString(event.absKickoffTime),
        isLive: event.isLive,
        homeTeam: {
            countryName: event.homeTeam?.countryName ?? '',
            teamName: event.homeTeam?.teamName.map(formatTranslation) ?? [],
        },
        awayTeam: {
            countryName: event.awayTeam?.countryName ?? '',
            teamName: event.awayTeam?.teamName.map(formatTranslation) ?? [],
        },
        showTime: event.showTime,
        showTimeType: event.showTimeType,
    };
}

export function formatOdds(odds: OddsTypeFragment): IOdds {
    const marketGroup = formatMarketGroup(odds.eventResult?.marketGroup);
    return {
        point: odds.point,
        betCondition: odds.betCondition,
        sortOrder: odds.sortOrder,
        isLive: odds.isLive,
        eventResult: formatEventResult(odds.eventResult),
        marketType: MarketType.fromOddsApi(odds.marketType),
        marketDisplayTypeName: `marketType_${odds.marketType}`,
        marketGroup,
        oddsId: odds.id,
        status: OddsStatus.fromOddsApi(odds.status),
        eventId: odds.eventResult?.eventId ?? 0,
        hasMixParlay: odds.hasMixParlay,
        priceOption: odds.eventResult && odds.eventResult.marketGroup ? getPriceOptions(
            odds.prices ?? [],
            odds.marketType,
            marketGroup,
            odds.isLive,
            odds.eventResult?.liveHomeScore ?? 0,
            odds.eventResult?.liveAwayScore ?? 0,
        ) : [],
    };
}

function formatEventResult(eventResult: Maybe<EventResultTypeFragment> | undefined): EventResult {
    return {
        eventId: eventResult?.eventId ?? 0,
        liveHomeScore: eventResult?.liveHomeScore ?? 0,
        liveAwayScore: eventResult?.liveAwayScore ?? 0,
    };
}

function formatMarketGroup(marketGroup: Maybe<MarketGroupTypeFragment> | undefined): IMarketGroup {
    return {
        id: marketGroup?.id ?? 0,
        name: marketGroup?.marketGroupName.map(formatTranslation) ?? [],
        homeTemplate: marketGroup?.homeTemplate ?? '',
        awayTemplate: marketGroup?.awayTemplate ?? '',
        homeExtension: marketGroup?.homeExtension.map(formatTranslation) ?? [],
        awayExtension: marketGroup?.awayExtension.map(formatTranslation) ?? [],
        displayPriority: marketGroup?.displayPriority ?? 0,
    };
}

export function formatOutrightTournament(outrightTournament: OutrightTournamentTypeFragment): IOutrightTournament {
    return {
        type: 'outright',
        eventDate: outrightTournament.eventDate,
        isLive: outrightTournament.isLive,
        id: outrightTournament.tournament?.id ?? 0,
        leagueName: outrightTournament.tournament?.tournamentName.map(formatTranslation) ?? [],
        leagueInfo: outrightTournament.tournament?.tournamentInfo.map(formatTranslation) ?? [],
        leagueCode: outrightTournament.tournament?.tournamentCode ?? '',
        sportType: outrightTournament.tournament?.sport?.sportType ? SportType.fromOddsApi(outrightTournament.tournament?.sport?.sportType) : SportType.Unknown,
        size: outrightTournament.tournament?.tournamentSize ? TournamentSize.fromOddsApi(outrightTournament.tournament?.tournamentSize) : TournamentSize.Unknown,
    };
}

export function formatOutright(outright: OutrightTypeFragment): IOutright {
    return {
        type: 'outright',
        id: outright.id,
        eventDate: outright.eventDate,
        eventCode: outright.eventCode,
        isLive: outright.isLive,
        isWalkinOnly: outright.isWalkinOnly,
        price: outright.price,
        teamName: outright.team?.teamName.map(formatTranslation) ?? [],
    };
}

export function formatTournamentCenterOutright(outright: TournamentCenterOutrightFragment): ITournamentOutright {
    return {
        type: 'outright',
        id: outright.id,
        eventDate: outright.eventDate,
        eventCode: outright.eventCode,
        isLive: outright.isLive,
        isWalkinOnly: outright.isWalkinOnly,
        price: outright.price,
        teamName: outright.team?.teamName.map(formatTranslation) ?? [],
        countryName: outright.team?.countryName ?? '',
    };
}

export function getMarketPage(event: { isLive: boolean, absKickoffTime?: string | null }) {
    if (event.isLive) {
        return MarketPage.Live;
    }
    return (event.absKickoffTime && isToday(event.absKickoffTime))
        ? MarketPage.Today
        : MarketPage.EarlyMarket;
}

export function formatTranslation(translation: TranslationFragment): ITranslation {
    return {
        language: LanguageType.fromOddsApi(translation.language),
        value: translation.value,
    };
}

export function formatRecommendationEvent(event: RecommendationEventFragment): IRecommendationEvent {
    return {
        eventId: event.id,
        leagueName: event.tournament.tournamentName.map(formatTranslation),
        leagueId: event.tournament.id,
        marketPage: getMarketPage(event),
        isLive: event.isLive,
        homeTeam: { teamName: event.homeTeam?.teamName.map(formatTranslation) ?? [] },
        awayTeam: { teamName: event.awayTeam?.teamName.map(formatTranslation) ?? [] },
        liveScore: {
            home: event.mainMarketEventResult.liveHomeScore,
            away: event.mainMarketEventResult.liveAwayScore,
        },
        sportType: event.tournament.sport?.sportType ? SportType.fromOddsApi(event.tournament.sport.sportType) : SportType.Unknown,
        eventResults: formatEventResultScores(event.eventResults),
    };
}
